import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatValue = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toFixed(num % 1 === 0 ? 0 : 2);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const change = payload[0].payload.change_percent;
  const changeParsed = change != null ? parseFloat(change) : null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <p className="text-sm font-medium text-gray-600">{label}</p>
      <p className="text-lg font-semibold text-blue-600">
        {formatValue(payload[0].value)}
      </p>
      {changeParsed != null && (
        <p
          className={`text-sm ${changeParsed >= 0 ? "text-green-600" : "text-red-600"}`}
        >
          {changeParsed >= 0 ? "+" : ""}
          {changeParsed.toFixed(1)}%
        </p>
      )}
    </div>
  );
};

export const MetricChart = ({ metric }) => {
  const { t } = useTranslation();
  const { display_name, period_type, data_points, summary } = metric;

  const chartData = (data_points || []).map((point) => ({
    name: formatDate(point.date),
    value: parseFloat(point.value),
    change_percent: point.change_percent,
  }));

  const gradientId = `gradient-${metric.metric_name}`;

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            {display_name}
          </h3>
          <p className="text-sm capitalize text-gray-500">{period_type}</p>
        </div>
        {summary && (
          <div className="flex gap-4 text-sm text-gray-500">
            <span>
              {t("common.avg", "Avg")}:{" "}
              <span className="font-medium">{formatValue(summary.avg)}</span>
            </span>
            <span>
              {t("common.sum", "Sum")}:{" "}
              <span className="font-medium">{formatValue(summary.sum)}</span>
            </span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1C64F2" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
            tickMargin={10}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
            tickFormatter={formatValue}
            tickMargin={10}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#1C64F2"
            strokeWidth={2}
            fill={`url(#${gradientId})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
